import { useState } from 'react'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { toast } from '@/lib/toast'
import { CrmApiError } from '../http'
import { deleteCategory } from './api'
import type { CrmCategory } from './types'

/**
 * Deleting a category (design §4.8, ED-10): one that is in use needs a choice,
 * a replacement from the same scope or "Geen categorie". No undo: the references
 * are re-pointed in the same transaction.
 *
 * `onChanged` asks the list to load again: after a delete, and when the API says the
 * category is in use after all (somebody used it since the list was loaded), because the
 * dialog was built on a count that is no longer true.
 */
export function DeleteCategoryDialog({
  category,
  options,
  onClose,
  onChanged,
}: {
  category: CrmCategory
  options: CrmCategory[]
  onClose: () => void
  onChanged: () => void
}) {
  const canReplace = options.length > 0
  // The only category of its scope has nothing to be replaced by.
  const [choice, setChoice] = useState<'replace' | 'clear'>(canReplace ? 'replace' : 'clear')
  const [replacementId, setReplacementId] = useState('')
  const [busy, setBusy] = useState(false)
  const total = category.usage.contacts + category.usage.organisations + (category.usage.lists ?? 0)

  async function confirm() {
    setBusy(true)
    try {
      await deleteCategory(
        category.id,
        total === 0 ? {} : choice === 'clear' ? { clear: true } : { replacementId },
      )
      toast.success(`Categorie '${category.name}' verwijderd.`)
      onChanged()
      onClose()
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Verwijderen mislukt.')
      if (caught instanceof CrmApiError && caught.code === 'category_in_use') {
        onChanged()
        onClose()
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Categorie '${category.name}' verwijderen?`}
      description={
        total === 0
          ? 'Deze categorie wordt nergens gebruikt.'
          : category.scope === 'lijst'
            ? `${total} ${total === 1 ? 'lijst heeft' : 'lijsten hebben'} deze soort. Wat gebeurt er met ${total === 1 ? 'die lijst' : 'die lijsten'}?`
            : `${total} ${total === 1 ? 'relatie heeft' : 'relaties hebben'} deze categorie. Wat gebeurt er met ${total === 1 ? 'die relatie' : 'die relaties'}?`
      }
      busy={busy}
      confirmDisabled={total > 0 && choice === 'replace' && !replacementId}
      onConfirm={() => void confirm()}
    >
      {total > 0 ? (
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="sr-only">{category.scope === 'lijst' ? 'Wat gebeurt er met de lijsten?' : 'Wat gebeurt er met de relaties?'}</legend>
          <label className="flex flex-wrap items-center gap-2">
            <input
              type="radio"
              name="keuze"
              checked={choice === 'replace'}
              disabled={!canReplace}
              onChange={() => setChoice('replace')}
            />
            Vervangen door
            <select
              aria-label="Vervangende categorie"
              value={replacementId}
              disabled={choice !== 'replace'}
              onChange={(event) => setReplacementId(event.target.value)}
              className="h-8 rounded-md border border-border bg-background px-2"
            >
              <option value="">Kies een categorie</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="keuze"
              checked={choice === 'clear'}
              onChange={() => setChoice('clear')}
            />
            {category.scope === 'lijst' ? 'Geen soort' : 'Geen categorie'}
          </label>
        </fieldset>
      ) : null}
    </ConfirmDialog>
  )
}
